import GumloopButton from '@/components/onboarding/components/GumloopButton';
import { FadeInText } from '@/components/ui/FadeInText';
import { useOnboarding } from '@/store/CedarStore';
import { LoginStep } from '@/store/onboardingSlice';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { itemVariants } from './animationVariants';

interface LoginStepRendererProps {
	step: LoginStep;
}

const LoginStepRenderer: React.FC<LoginStepRendererProps> = ({ step }) => {
	const onboarding = useOnboarding();
	const { renderStepText, setOnboardingStepValue, nextStep, setVariable } =
		onboarding;

	const [email, setEmail] = useState(step.email?.prefill || '');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(step.errorMessage || null);
	const [loading, setLoading] = useState(false);
	const [showEmailForm, setShowEmailForm] = useState(
		step.email?.emailOnly ||
			step.email?.prefill ||
			step.id === 'signin' ||
			false
	);
	const [isMounted, setIsMounted] = useState(false);
	const [showDescription, setShowDescription] = useState(false);

	// Reset state when component mounts/unmounts
	useEffect(() => {
		setIsMounted(true);
		const timer = setTimeout(() => {
			setShowDescription(true);
		}, 800);

		return () => {
			setIsMounted(false);
			setPassword('');
			setError(null);
			setLoading(false);
			setShowDescription(false);
			clearTimeout(timer);
		};
	}, []);

	// Handle auto sign-in
	useEffect(() => {
		if (!isMounted || !step.autoSignin) return;

		const autoSignIn = async () => {
			if (step.google?.onSubmit) {
				await handleSignIn('google');
			} else if (step.github?.onSubmit) {
				await handleSignIn('github');
			} else if (step.okta?.onSubmit) {
				await handleSignIn('okta');
			} else if (step.microsoft?.onSubmit) {
				await handleSignIn('microsoft');
			}
		};

		autoSignIn();
	}, [isMounted, step.autoSignin]);

	// Handle case where no login methods are available
	useEffect(() => {
		const hasGoogleLogin = Boolean(step.google?.onSubmit);
		const hasGithubLogin = Boolean(step.github?.onSubmit);
		const hasOktaLogin = Boolean(step.okta?.onSubmit);
		const hasMicrosoftLogin = Boolean(step.microsoft?.onSubmit);
		const hasEmailLogin = Boolean(step.email?.onSubmit);
		const hasAnyLogin =
			hasGoogleLogin ||
			hasGithubLogin ||
			hasOktaLogin ||
			hasMicrosoftLogin ||
			hasEmailLogin;

		if (!hasAnyLogin) {
			nextStep();
		}
	}, [step, nextStep]);

	const isValidEmail = (email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const isValidPassword = (password: string) => {
		return password.length >= 6;
	};

	const getFormattedErrorMessage = (errorMessage: string): string => {
		switch (errorMessage) {
			case 'Firebase: Error (auth/email-already-in-use).':
				return 'Email already in use. Please sign in';
			case 'auth/unverified-email':
				return 'Email not verified. Please check your email for a verification link.';
			case 'Firebase: Error (auth/invalid-login-credentials).':
				return 'Invalid email or password. Please try again.';
			case 'auth/invalid-email':
				return 'Invalid email. Please enter a valid email address.';
			case 'auth/weak-password':
				return 'Password must be at least 6 characters long.';
			case 'Firebase: Error (auth/cancelled-popup-request).':
				return 'Sign in cancelled. Please try again.';
			case 'Firebase: Error (auth/popup-closed-by-user).':
				return 'Sign in cancelled. Please try again.';
			default:
				return errorMessage;
		}
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmail = e.target.value;
		setEmail(newEmail);
		setError(null);
	};

	const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const email = e.target.value;
		if (email) {
			if (!isValidEmail(email)) {
				setError('Please enter a valid email address');
				return;
			}

			// Run custom validations if they exist
			if (step.email?.validation) {
				for (const validation of step.email.validation) {
					if (!validation.validationMethod(email)) {
						setError(validation.errorMessage);
						return;
					}
				}
			}
		}
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newPassword = e.target.value;
		setPassword(newPassword);
		setError(null);
	};

	const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const password = e.target.value;
		if (password && !isValidPassword(password)) {
			setError('Password must be at least 6 characters long');
		}
	};

	const handleEmailSignIn = async () => {
		if (!isMounted) return;
		if (!email) {
			setError('Email is required.');
			return;
		}

		if (!isValidEmail(email)) {
			setError('Please enter a valid email address');
			return;
		}

		if (!step.email?.emailOnly && !isValidPassword(password)) {
			setError('Password must be at least 6 characters long');
			return;
		}

		setError(null);
		setLoading(true);
		try {
			let result: string | false | { errorMessage: string } = false;
			if (step.email?.onSubmit) {
				result = await step.email.onSubmit(
					email,
					step.email.emailOnly ? '' : password,
					onboarding
				);
			}
			if (typeof result === 'string') {
				setError(null);
				setOnboardingStepValue(result);
				setVariable(
					'endDescription',
					'Account successfully created. Check your inbox for a verification email from no-reply@email.gumloop.com'
				);
				nextStep();
			} else if (typeof result === 'object' && result.errorMessage) {
				// Handle different Firebase auth error cases with user-friendly messages
				setError(getFormattedErrorMessage(result.errorMessage));
			} else {
				setError('Email already in use.');
			}
		} catch {
			setError('An error occurred. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleSignIn = async (
		provider: 'google' | 'github' | 'okta' | 'microsoft'
	) => {
		if (!isMounted) return;
		setError(null);
		setLoading(true);
		try {
			let result: string | false | { errorMessage: string } = false;
			if (provider === 'google' && step.google?.onSubmit) {
				result = await step.google.onSubmit(onboarding);
			} else if (provider === 'github' && step.github?.onSubmit) {
				result = await step.github.onSubmit(onboarding);
			} else if (provider === 'okta' && step.okta?.onSubmit) {
				result = await step.okta.onSubmit(onboarding);
			} else if (provider === 'microsoft' && step.microsoft?.onSubmit) {
				result = await step.microsoft.onSubmit(onboarding);
			}
			if (typeof result === 'string') {
				setOnboardingStepValue(result);
				nextStep();
			} else if (typeof result === 'object' && result.errorMessage) {
				setError(getFormattedErrorMessage(result.errorMessage));
			} else {
				setError('Login failed. Please try again.');
			}
		} catch {
			setError('An error occurred. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const hasGoogleLogin = Boolean(step.google?.onSubmit);
	const hasGithubLogin = Boolean(step.github?.onSubmit);
	const hasOktaLogin = Boolean(step.okta?.onSubmit);
	const hasMicrosoftLogin = Boolean(step.microsoft?.onSubmit);
	const hasEmailLogin = Boolean(step.email?.onSubmit);
	const hasAnyLogin =
		hasGoogleLogin ||
		hasGithubLogin ||
		hasOktaLogin ||
		hasMicrosoftLogin ||
		hasEmailLogin;

	if (!hasAnyLogin) {
		return null;
	}

	return (
		<div className='w-full max-w-md mx-auto flex flex-col items-center justify-center'>
			<motion.h2
				className='text-xl font-bold mb-2 text-center'
				variants={itemVariants}
				layoutId='login-title'
				key='login-title'>
				{renderStepText(step.title)}
			</motion.h2>
			{step.description && showDescription && (
				<motion.p
					className='text-md mb-4 text-center'
					variants={itemVariants}
					layoutId='login-description'
					key='login-description'>
					<FadeInText text={step.description} delay={0} />
				</motion.p>
			)}
			{(hasGoogleLogin ||
				hasGithubLogin ||
				hasOktaLogin ||
				hasMicrosoftLogin) && (
				<motion.div
					className='mt-6 flex flex-col gap-4 w-full'
					variants={itemVariants}
					layoutId='social-login-buttons'>
					{(hasGoogleLogin || hasGithubLogin || hasMicrosoftLogin) && (
						<div
							className={`grid gap-4 w-full ${
								(hasGoogleLogin && hasGithubLogin && !hasMicrosoftLogin) ||
								(hasGoogleLogin && !hasGithubLogin && hasMicrosoftLogin) ||
								(!hasGoogleLogin && hasGithubLogin && hasMicrosoftLogin)
									? 'grid-cols-2'
									: 'grid-cols-1'
							}`}>
							{hasGoogleLogin && (
								<button
									onClick={async () => await handleSignIn('google')}
									className='bg-white text-foreground focus-visible:outline-accent-foreground flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border py-3 focus-visible:outline-2 focus-visible:outline-offset-2'
									aria-label='Sign in with Google'
									tabIndex={0}>
									<svg className='h-5 w-5' viewBox='0 0 186.69 190.5'>
										<g transform='translate(1184.583 765.171)'>
											<path
												clipPath='none'
												mask='none'
												d='M-1089.333-687.239v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z'
												fill='#4285f4'
											/>
											<path
												clipPath='none'
												mask='none'
												d='M-1142.714-651.791l-6.972 5.337-24.679 19.223h0c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z'
												fill='#34a853'
											/>
											<path
												clipPath='none'
												mask='none'
												d='M-1174.365-712.61c-6.494 12.815-10.217 27.276-10.217 42.689s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z'
												fill='#fbbc05'
											/>
											<path
												d='M-1089.333-727.244c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276c-16.539-15.413-38.013-24.852-63.731-24.852-37.234 0-69.359 21.388-85.032 52.561l31.692 24.592c7.533-22.514 28.575-39.226 53.34-39.226z'
												fill='#ea4335'
												clipPath='none'
												mask='none'
											/>
										</g>
									</svg>
									<span className='text-foreground text-sm leading-6 font-semibold'>
										{step.id === 'signup'
											? 'Google (Use work email)'
											: 'Sign in with Google'}
									</span>
								</button>
							)}
							{hasGithubLogin && (
								<button
									onClick={() => handleSignIn('github')}
									className='flex w-full cursor-pointer items-center justify-center gap-3 rounded-md bg-[#24292F] px-3 py-3 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F]'
									aria-label='Sign in with GitHub'
									tabIndex={0}>
									<svg
										className='h-5 w-5'
										aria-hidden='true'
										fill='currentColor'
										viewBox='0 0 20 20'>
										<path
											fillRule='evenodd'
											d='M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z'
											clipRule='evenodd'
										/>
									</svg>
									<span className='text-sm leading-6 font-semibold'>
										GitHub
									</span>
								</button>
							)}
							{hasMicrosoftLogin && (
								<button
									onClick={() => handleSignIn('microsoft')}
									className='bg-white text-foreground focus-visible:outline-accent-foreground flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border py-3 focus-visible:outline-2 focus-visible:outline-offset-2'
									aria-label='Sign in with Microsoft'
									tabIndex={0}>
									<svg className='h-5 w-5' viewBox='0 0 23 23'>
										<path fill='#f3f3f3' d='M0 0h23v23H0z' />
										<path fill='#f35325' d='M1 1h10v10H1z' />
										<path fill='#81bc06' d='M12 1h10v10H12z' />
										<path fill='#05a6f0' d='M1 12h10v10H1z' />
										<path fill='#ffba08' d='M12 12h10v10H12z' />
									</svg>
									<span className='text-foreground text-sm leading-6 font-semibold'>
										Microsoft
									</span>
								</button>
							)}
						</div>
					)}
					{hasOktaLogin && (
						<button
							onClick={() => handleSignIn('okta')}
							className='bg-background border-border focus:ring-accent-foreground flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border px-3 py-3 shadow-md hover:bg-gray-100 focus:ring-2 focus:outline-none'
							aria-label='Sign in with Okta'
							tabIndex={0}>
							<span className='text-sm leading-6 font-semibold text-black'>
								Sign in with Okta
							</span>
							<img
								src='/images/okta-logo.png'
								alt='Okta logo'
								width={20}
								height={20}
								className='object-contain'
							/>
						</button>
					)}
				</motion.div>
			)}
			{hasEmailLogin && (
				<>
					{step.id === 'signin' &&
						(hasGoogleLogin ||
							hasGithubLogin ||
							hasOktaLogin ||
							hasMicrosoftLogin) && (
							<motion.div
								className='mt-6 flex items-center w-full'
								variants={itemVariants}
								layoutId='or-divider'>
								<div className='flex-grow h-px bg-gray-200' />
								<span className='mx-2 text-xs text-gray-400'>Or</span>
								<div className='flex-grow h-px bg-gray-200' />
							</motion.div>
						)}
					{showEmailForm && (
						<>
							<motion.div
								className='flex flex-col gap-1 mt-6 w-full'
								variants={itemVariants}
								layoutId='email-input'>
								<Label htmlFor='email' className='text-left mb-1'>
									Email address
								</Label>
								<Input
									id='email'
									type='email'
									value={email}
									onChange={handleEmailChange}
									onBlur={handleEmailBlur}
									placeholder={step.email?.placeholder || 'Enter your email'}
									className='w-full'
									aria-label='Email address'
									tabIndex={0}
									required
								/>
							</motion.div>
							{!step.email?.emailOnly && (
								<motion.div
									className='flex flex-col gap-1 mt-4 w-full'
									variants={itemVariants}
									layoutId='password-input'>
									<Label htmlFor='password' className='text-left mb-1'>
										Password
									</Label>
									<Input
										id='password'
										type='password'
										value={password}
										onChange={handlePasswordChange}
										onBlur={handlePasswordBlur}
										placeholder='Enter your password'
										className='w-full'
										aria-label='Password'
										tabIndex={0}
										required
									/>
								</motion.div>
							)}
							{error && (
								<motion.p
									className='text-sm text-red-500 mt-1 font-semibold'
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.3, delay: 0.1 }}
									variants={itemVariants}>
									{error}
								</motion.p>
							)}
						</>
					)}
					<motion.div
						variants={itemVariants}
						className='w-full'
						layoutId='email-login-button'>
						{!showEmailForm ? (
							<GumloopButton
								onClick={() => setShowEmailForm(true)}
								className='mt-4 w-full'
								aria-label='Sign in with email'
								disabled={loading}
								text={
									step.buttonText ||
									(step.id === 'signup'
										? 'Create account with email'
										: 'Sign in with email')
								}
							/>
						) : (
							<GumloopButton
								onClick={handleEmailSignIn}
								className='mt-4 w-full'
								aria-label='Sign in'
								disabled={loading}
								text={
									step.buttonText ||
									(loading
										? step.id === 'signup'
											? 'Creating account...'
											: 'Signing in...'
										: step.id === 'signup'
											? 'Create account'
											: 'Sign in')
								}
							/>
						)}
					</motion.div>
				</>
			)}
		</div>
	);
};

export default LoginStepRenderer;
